import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "@/features/axiosHelper";

const homepageRecordsSlice = createSlice({
  name: "homepageRecords",
  initialState: {
    records: [],
    status: "idle",
    dataLoaded: false,
    selectedRows: [],
    optionalWorkflows: [],
    workflowToEnabledConnectionPairMap: {},
  },
  reducers: {
    setRecords: (state, action) => {
      const { records } = action.payload;
      state.records = records;
    },
    setSelectedRows: (state, action) => {
      const { selectedRows } = action.payload;
      state.selectedRows = selectedRows;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getRecords.pending, (state, action) => {
        state.status = "loading";
        state.dataLoaded = false;
      })
      .addCase(getRecords.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.error.message;
        state.dataLoaded = true;
      })
      .addCase(getRecords.fulfilled, (state, action) => {
        state.status = "loaded";
        const {
          Records,
          OptionalWorkflows,
          WorkflowToEnabledConnectionPairMap,
        } = action.payload;
        state.records = Records;
        state.optionalWorkflows = OptionalWorkflows;
        state.workflowToEnabledConnectionPairMap =
          WorkflowToEnabledConnectionPairMap;
        state.dataLoaded = true;
      });
  },
});

//SHEP-82: These are used togeather with extraReducers to make HTTP call from any component that import this method.
// Also the redux state can be updated here itself after receiving the HTTP reponse.
export const getRecords = createAsyncThunk(
  "homepageRecords/getRecords",
  async (params) => {
    const { selectedConnectionId, startDate, endDate, searchString } = params;
    //const state = getState();
    const response = await axios.get("/recordgroup", {
      params: {
        selectedConnectionId: selectedConnectionId,
        startDate: startDate,
        endDate: endDate,
        searchFilter: searchString,
        clearCache: false,
      },
    });

    return response.data;
  }
);

export const getExportData = createAsyncThunk(
  "homepageRecords/getExportData",
  async (params, { rejectWithValue }) => {
    try {
      const { selectedRows, selectedConnection, startDate, endDate } = params;
      const response = await axios.post(
        "/recordgroup/export-record-data",
        null,
        {
          params: {
            selectedConnectionId: selectedConnection,
            startDate: startDate,
            endDate: endDate,
            selectedRowIds:
              selectedRows.length > 0 ? selectedRows.join(",") : "",
          },
          responseType: "blob",
        }
      );
      const now = new Date();
      const dateValue = `${now.getFullYear()}-${(now.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${now.getDate().toString().padStart(2, "0")}_${now
        .getHours()
        .toString()
        .padStart(2, "0")}-${now.getMinutes().toString().padStart(2, "0")}-${now
        .getSeconds()
        .toString()
        .padStart(2, "0")}-${now //padding others to 2 digits
        .getMilliseconds()
        .toString()
        .padStart(3, "0")}`; //padding milliseconds to 3 digits
      const fileName = `RecordsData_${dateValue}.csv`;
      // Create a downloadable link for the CSV file
      const blob = new Blob([response.data], { type: "text/csv" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.style.display = "none";
      link.setAttribute("download", fileName); // Set the file name
      document.body.appendChild(link);
      link.click();

      // Clean up the temporary URL object to clean up the space
      window.URL.revokeObjectURL(url);
      return true;
    } catch (error) {
      console.error("Error exporting data:", error);
      return rejectWithValue(
        error.response?.data?.message ||
          error.message ||
          "An unknown error occurred."
      );
    }
  }
);

//SHEP-82: These are some helper methods that can be easily used to read the redux state into any component that import this method.
// Selectors
export const selectRecords = (state) => state.tempInfo.homepageRecords.records;
export const selectIsDataLoaded = (state) =>
  state.tempInfo.homepageRecords.dataLoaded;
export const selectSelectedRows = (state) =>
  state.tempInfo.homepageRecords.selectedRows;
export const selectOptionalWorkflows = (state) =>
  state.tempInfo.homepageRecords.optionalWorkflows;
export const selectWorkflowToEnabledConnectionPairMap = (state) =>
  state.tempInfo.homepageRecords.workflowToEnabledConnectionPairMap;

export const { setRecords, setSelectedRows } = homepageRecordsSlice.actions;

export default homepageRecordsSlice.reducer;
